/**
 * text-prompt-modal — async замена native browser prompt().
 *
 * Использует существующие .crm-modal-overlay стили из CRM shell.
 * Возвращает Promise<string|null>:
 *   - string  — введённый текст (может быть пустой строкой если allowEmpty=true)
 *   - null    — пользователь cancel'нул
 *
 * Usage:
 *   const comment = await promptText({
 *       title: 'Перевести в "Подтверждена"',
 *       message: 'Комментарий (необязательно)',
 *       placeholder: 'Например: клиент согласился',
 *       confirmLabel: 'Подтвердить',
 *       cancelLabel: 'Отмена',
 *       allowEmpty: true,        // позволяет пустой submit (Native prompt по умолчанию запрещает)
 *       initialValue: ''
 *   });
 *   if (comment === null) return;  // cancel
 *
 * @module components/text-prompt-modal
 */

'use strict';

const _t = (k, fb) => {
    const v = window.i18n?.t?.(k);
    return (v && v !== k) ? v : (fb || k);
};

function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
}

/**
 * Open a single-line text prompt modal. Returns Promise<string | null>.
 * @param {Object} opts
 */
export function promptText(opts = {}) {
    return new Promise((resolve) => {
        const title = opts.title || _t('common.input', 'Введите значение');
        const message = opts.message || '';
        const placeholder = opts.placeholder || '';
        const initialValue = opts.initialValue ?? '';
        const allowEmpty = opts.allowEmpty !== false;
        const requireText = !allowEmpty;
        const confirmLabel = opts.confirmLabel || _t('btn.submit', 'Подтвердить');
        const cancelLabel = opts.cancelLabel || _t('btn.cancel', 'Отмена');
        const multiline = !!opts.multiline;

        const overlay = document.createElement('div');
        overlay.className = 'crm-modal-overlay';
        overlay.style.cssText = 'position:fixed;inset:0;background:rgba(15,23,42,0.45);display:flex;align-items:center;justify-content:center;z-index:10000;backdrop-filter:blur(2px);';

        const modal = document.createElement('div');
        modal.className = 'crm-modal text-prompt-modal';
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');
        modal.style.cssText = 'background:white;border-radius:8px;max-width:480px;width:92%;box-shadow:0 16px 48px rgba(0,0,0,0.24);overflow:hidden;animation:crm-modal-in 0.15s ease-out;';

        const inputHtml = multiline
            ? `<textarea class="text-prompt-input" rows="4" placeholder="${escapeHtml(placeholder)}" style="width:100%;padding:8px 10px;border:1px solid #e2e8f0;border-radius:6px;font-size:13px;font-family:inherit;resize:vertical;min-height:80px;">${escapeHtml(initialValue)}</textarea>`
            : `<input type="text" class="text-prompt-input" placeholder="${escapeHtml(placeholder)}" value="${escapeHtml(initialValue)}" style="width:100%;padding:8px 10px;border:1px solid #e2e8f0;border-radius:6px;font-size:14px;" />`;

        modal.innerHTML = `
            <div class="text-prompt-header" style="padding:14px 18px 0;display:flex;align-items:flex-start;justify-content:space-between;gap:10px;">
                <h3 style="margin:0;font-size:15px;font-weight:600;color:#0f172a;line-height:1.4;">${escapeHtml(title)}</h3>
                <button class="text-prompt-close" type="button" aria-label="${escapeHtml(cancelLabel)}" style="background:transparent;border:0;font-size:20px;line-height:1;color:#94a3b8;cursor:pointer;padding:2px 6px;border-radius:4px;">×</button>
            </div>
            <div class="text-prompt-body" style="padding:10px 18px 14px;">
                ${message ? `<div style="margin-bottom:10px;font-size:13px;color:#64748b;line-height:1.4;">${escapeHtml(message)}</div>` : ''}
                ${inputHtml}
                <div class="text-prompt-error" style="display:none;margin-top:6px;font-size:12px;color:#dc2626;"></div>
            </div>
            <div class="text-prompt-footer" style="padding:0 18px 16px;display:flex;justify-content:flex-end;gap:8px;">
                <button class="btn-ghost text-prompt-cancel" type="button" style="padding:7px 14px;background:transparent;border:1px solid #e2e8f0;border-radius:6px;color:#475569;font-size:13px;cursor:pointer;">${escapeHtml(cancelLabel)}</button>
                <button class="btn-primary text-prompt-confirm" type="button" style="padding:7px 14px;background:#2563eb;color:white;border:0;border-radius:6px;font-size:13px;font-weight:500;cursor:pointer;">${escapeHtml(confirmLabel)}</button>
            </div>
        `;

        overlay.appendChild(modal);
        document.body.appendChild(overlay);

        const input = modal.querySelector('.text-prompt-input');
        const errorEl = modal.querySelector('.text-prompt-error');
        const closeBtn = modal.querySelector('.text-prompt-close');
        const cancelBtn = modal.querySelector('.text-prompt-cancel');
        const confirmBtn = modal.querySelector('.text-prompt-confirm');

        let resolved = false;
        const finish = (value) => {
            if (resolved) return;
            resolved = true;
            try { document.removeEventListener('keydown', onKeyDown, true); } catch (_) {}
            try { overlay.remove(); } catch (_) {}
            resolve(value);
        };

        const submit = () => {
            const v = input.value;
            if (requireText && !v.trim()) {
                errorEl.textContent = _t('common.required_field', 'Обязательное поле');
                errorEl.style.display = '';
                input.focus();
                return;
            }
            finish(v);
        };

        closeBtn.addEventListener('click', () => finish(null));
        cancelBtn.addEventListener('click', () => finish(null));
        confirmBtn.addEventListener('click', submit);
        overlay.addEventListener('click', (e) => { if (e.target === overlay) finish(null); });

        const onKeyDown = (e) => {
            if (e.key === 'Escape') { e.preventDefault(); finish(null); }
            else if (e.key === 'Enter' && !multiline && document.activeElement === input) {
                e.preventDefault();
                submit();
            }
        };
        document.addEventListener('keydown', onKeyDown, true);

        // autofocus + select existing text
        setTimeout(() => {
            input.focus();
            try { input.select(); } catch (_) {}
        }, 50);
    });
}

/**
 * Confirmation dialog (sync replacement для confirm()).
 * Returns Promise<boolean>.
 */
export function confirmModal(opts = {}) {
    return new Promise((resolve) => {
        const title = opts.title || _t('common.confirm', 'Подтверждение');
        const message = opts.message || '';
        const confirmLabel = opts.confirmLabel || _t('btn.submit', 'Подтвердить');
        const cancelLabel = opts.cancelLabel || _t('btn.cancel', 'Отмена');
        const danger = !!opts.danger;

        const overlay = document.createElement('div');
        overlay.className = 'crm-modal-overlay';
        overlay.style.cssText = 'position:fixed;inset:0;background:rgba(15,23,42,0.45);display:flex;align-items:center;justify-content:center;z-index:10000;';

        const modal = document.createElement('div');
        modal.className = 'crm-modal confirm-modal';
        modal.style.cssText = 'background:white;border-radius:8px;max-width:420px;width:92%;box-shadow:0 16px 48px rgba(0,0,0,0.24);overflow:hidden;';

        modal.innerHTML = `
            <div style="padding:16px 18px;">
                <h3 style="margin:0 0 8px;font-size:15px;font-weight:600;color:#0f172a;">${escapeHtml(title)}</h3>
                ${message ? `<div style="font-size:13px;color:#475569;line-height:1.5;">${escapeHtml(message)}</div>` : ''}
            </div>
            <div style="padding:0 18px 16px;display:flex;justify-content:flex-end;gap:8px;">
                <button class="confirm-cancel" type="button" style="padding:7px 14px;background:transparent;border:1px solid #e2e8f0;border-radius:6px;color:#475569;font-size:13px;cursor:pointer;">${escapeHtml(cancelLabel)}</button>
                <button class="confirm-ok" type="button" style="padding:7px 14px;background:${danger ? '#dc2626' : '#2563eb'};color:white;border:0;border-radius:6px;font-size:13px;font-weight:500;cursor:pointer;">${escapeHtml(confirmLabel)}</button>
            </div>
        `;
        overlay.appendChild(modal);
        document.body.appendChild(overlay);

        const finish = (val) => {
            try { document.removeEventListener('keydown', onKeyDown, true); } catch (_) {}
            overlay.remove();
            resolve(val);
        };
        modal.querySelector('.confirm-cancel').addEventListener('click', () => finish(false));
        modal.querySelector('.confirm-ok').addEventListener('click', () => finish(true));
        overlay.addEventListener('click', (e) => { if (e.target === overlay) finish(false); });
        const onKeyDown = (e) => {
            if (e.key === 'Escape') finish(false);
            else if (e.key === 'Enter') finish(true);
        };
        document.addEventListener('keydown', onKeyDown, true);
        setTimeout(() => modal.querySelector('.confirm-ok').focus(), 50);
    });
}
